import type { PropLine } from '@sharpr/pricing';
import { withCut, type GameCut, type Seed, type SeedGame, type SeedPlayer } from '@sharpr/projections';
import { reached, type Delta, type StreamPosition, type Tombstone } from '@sharpr/board';

// The board a client holds for one sport (spec §13.3–13.4), cached in two halves along what each depends on: the prop
// lines, the same for every viewer and kept current by the board's stream, and the viewer's projections (player fields
// and their games, flat by key, and the seed's metadata), cut per view and replaced on a reseed. A seed carries both, its
// lines nested under its games' players; mergeLines and mergeProjections
// fold it into each half under one rule, newer wins, so the order things arrive in never matters. Lines (and tombstones)
// are versioned by the worker's clock; projections, which carry no clock of their own, by the stream position their seed
// was read at.
export type BoardGame = Omit<SeedGame, 'players'>;
export type BoardPlayer = Omit<SeedPlayer, 'propLines'> & { gameId: string };
export interface BoardLines {
  lines: ReadonlyMap<string, PropLine>;     // by `${key}|${book}`
  tombstones: ReadonlyMap<string, number>;  // gameId → updatedAt
  position?: StreamPosition;                // the stream position the lines are current to: where the stream resumes
}
export interface BoardProjections {
  seed: Omit<Seed, 'games'>;
  games: ReadonlyMap<string, BoardGame>;       // by game id
  players: ReadonlyMap<string, BoardPlayer>;   // by boardPlayerKey
  // Players a delta named that a seed read after it still lacked (a game past its start time but not yet closed): their
  // deltas are dropped without asking again until a seed holds them.
  absent: ReadonlySet<string>;
  // From a seed that lacked the paid data its view carries: the board asks again.
  degraded?: true;
}
// On a seed that lacks the paid data its view would carry: the public view served while the access check was unavailable
// (no paid data without a decision; OWASP ASVS 4.1.5), or a paid view built before any cut was stored (cutover). It lands
// only on a board that holds no paid projections, so an open board never loses them (as a cache serves its last good
// copy on an origin error, RFC 5861 stale-if-error); a board that does keeps them and asks again.
export const BOARD_DEGRADED_HEADER = 'X-Board-Degraded';
export const takesDegradedSeed = (held: BoardProjections | undefined): boolean => !held || held.seed.view === 'public';
// What the odds screen prices: the viewer's projections over the shared lines.
export interface Board extends BoardProjections { lines: ReadonlyMap<string, PropLine> }

export const lineId = (l: PropLine): string => `${l.key}|${l.book}`;
export const boardPlayerKey = (gameId: string, playerId: string): string => `${gameId}|${playerId}`;
// A prop key (`propKey`) is the player key plus the market: the game, the player, then the market.
export const playerOf = (l: PropLine): string => l.key.slice(0, l.key.lastIndexOf('|'));
const gameOf = (l: PropLine): string => l.key.slice(0, l.key.indexOf('|'));
const isTombstone = (x: PropLine | Tombstone): x is Tombstone => !('key' in x);
const closed = (tombstones: ReadonlyMap<string, number>, l: PropLine) => (tombstones.get(gameOf(l)) ?? -Infinity) >= l.updatedAt;
// The seed is at or after what is held (no held position: nothing to compare with).
const atOrAfter = (seed: Seed, held?: StreamPosition) => !held || reached(seed.position, held);

// A seed's lines merged into the held ones: per line, the newer copy wins (a line a delta moved while the seed was
// fetched keeps its newer price; on a tie, a newer seed's copy). A game leaves only through its tombstone (the worker
// closes it at tip-off), never because a seed doesn't list it: a game listed after the seed was built may already have
// lines, and a started game's lines are never shown, since its players leave the projections. Tombstones outlive seeds,
// which may still list a game the worker closed. The position becomes the later of the two.
export function mergeLines(held: BoardLines | undefined, seed: Seed): BoardLines {
  const lines = new Map<string, PropLine>(held?.lines);
  const newer = atOrAfter(seed, held?.position);
  for (const { propLines } of seed.games.flatMap(g => g.players)) {
    for (const l of propLines) {
      const h = lines.get(lineId(l));
      if (!h || l.updatedAt > h.updatedAt || (newer && l.updatedAt === h.updatedAt)) lines.set(lineId(l), l);
    }
  }
  const tombstones = new Map(held?.tombstones);
  for (const [id, l] of lines) if (closed(tombstones, l)) lines.delete(id);
  return { lines, tombstones, position: newer ? seed.position ?? held?.position : held?.position };
}

// A seed's projections, if it is at or after the held ones or of another view (access gained or lost); an older copy
// changes nothing, so projections never roll back. Within the view, a game the board took a newer cut of while the seed
// was in flight keeps that cut, its players and the config it brought: positions order seeds, not cuts. `awaiting`:
// players a delta named that the board lacked, by the delta's position — still missing from a seed read at or after
// it, they are absent.
export function mergeProjections(held: BoardProjections | undefined, seed: Seed, awaiting: ReadonlyMap<string, StreamPosition> = new Map()): BoardProjections {
  const same = held !== undefined && held.seed.view === seed.view;
  if (same && !atOrAfter(seed, held.seed.position)) return held;
  const { games: seedGames, ...meta } = seed;
  const games = new Map<string, BoardGame>();
  const players = new Map<string, BoardPlayer>();
  for (const { players: gamePlayers, ...g } of seedGames) {
    const kept = same ? held.games.get(g.gameId) : undefined;
    if (kept && newerGame(kept, g)) {
      games.set(g.gameId, kept);
      for (const [k, p] of held!.players) if (p.gameId === g.gameId) players.set(k, p);
    } else games.set(g.gameId, g);
    for (const { propLines: _lines, ...p } of gamePlayers) {
      const k = boardPlayerKey(g.gameId, p.playerId);
      if (!players.has(k)) players.set(k, { ...p, gameId: g.gameId });
    }
  }
  if (same && (held.seed.sportVersion ?? -1) > (meta.sportVersion ?? -1)) meta.sportVersion = held.seed.sportVersion;
  if (same && olderConfig(meta, held.seed)) {
    meta.configVersion = held.seed.configVersion;
    meta.releaseTime = held.seed.releaseTime;
    meta.oddsRange = held.seed.oddsRange;
  }
  const absent = new Set([...(held?.absent ?? [])].filter(k => !players.has(k)));
  for (const [k, at] of awaiting) if (!players.has(k) && seed.position && reached(seed.position, at)) absent.add(k);
  return { seed: meta, games, players, absent };
}

// The sport's config (release time, odds range) is ordered by the sport version it was read at, apart from the cuts':
// a seed's is older than the held one only when both are labeled and its label is lower; one read live (unlabeled: no
// manifest to key it by) is the newest there is.
const olderConfig = (a: { configVersion?: number }, b: { configVersion?: number }) =>
  a.configVersion !== undefined && b.configVersion !== undefined && a.configVersion < b.configVersion;

// A cut is newer than the held game by its game's version, then the sport's (a config change re-cuts a game unchanged).
// A game a seed carried without a cut (odds only) was so as of the seed's sport version: it takes a cut from then on.
const newerCut = (cut: GameCut, held: BoardGame, seedSportVersion = -1) =>
  held.version === undefined ? cut.sportVersion >= seedSportVersion
    : cut.version !== held.version ? cut.version > held.version : cut.sportVersion > (held.sportVersion ?? -1);

// A held game is newer than a seed's copy of it by the cut each was joined with, as newerCut orders cuts; a seed's game
// without a cut always wins.
const newerGame = (held: BoardGame, seed: BoardGame) =>
  held.version !== undefined && seed.version !== undefined
  && (held.version !== seed.version ? held.version > seed.version : (held.sportVersion ?? -1) > (seed.sportVersion ?? -1));

// The changed games' cuts a reseed named, each over the players the board holds of its game, as a seed would join it
// (withCut): a newer cut replaces its game's projection and its players' fields as a unit; an older or repeated one, a
// game the board doesn't hold (its players come with a seed), or another view's cut changes nothing. The seed's
// position stays: it orders seeds, and a cut is no seed.
export function mergeGameCuts(held: BoardProjections, cuts: readonly GameCut[]): BoardProjections {
  const fresh = cuts.filter(c => {
    const g = held.games.get(c.gameId);
    return c.view === held.seed.view && g !== undefined && newerCut(c, g, held.seed.sportVersion);
  });
  if (fresh.length === 0) return held;
  const games = new Map(held.games);
  const players = new Map(held.players);
  const seed = { ...held.seed };
  let sportVersion = Math.max(held.seed.sportVersion ?? -1, ...[...held.games.values()].map(g => g.sportVersion ?? -1));
  for (const cut of fresh) {
    if (!newerCut(cut, games.get(cut.gameId)!, held.seed.sportVersion)) continue;
    const own = [...players.values()].filter(p => p.gameId === cut.gameId);
    const { players: joined, ...game } = withCut(cut, own);
    games.set(cut.gameId, { gameId: cut.gameId, ...game });
    for (const p of joined) players.set(boardPlayerKey(cut.gameId, p.playerId), p);
    if (cut.sportVersion > sportVersion) {
      sportVersion = cut.sportVersion;
      seed.sportVersion = cut.sportVersion;
    }
    // The sport's config rides every cut, read at the cut's sport version: a newer one than the board's replaces it.
    if (!olderConfig({ configVersion: cut.sportVersion }, seed) && cut.sportVersion !== seed.configVersion) {
      seed.configVersion = cut.sportVersion;
      seed.releaseTime = cut.releaseTime;
      seed.oddsRange = cut.oddsRange;
    }
    if (game.projection && cut.lastUpdated && (!seed.lastUpdated || Date.parse(cut.lastUpdated) > Date.parse(seed.lastUpdated))) seed.lastUpdated = cut.lastUpdated;
  }
  return { seed, games, players, absent: held.absent };
}

// Deltas into the held lines: a line replaces the held one only when strictly newer, so one a seed already holds
// changes nothing; a tombstone drops every held line of its game older than it.
export function applyDeltas(held: BoardLines, deltas: readonly Delta[]): BoardLines {
  const lines = new Map(held.lines);
  const tombstones = new Map(held.tombstones);
  for (const delta of deltas) {
    for (const x of delta.propLines) {
      if (isTombstone(x)) {
        if (x.updatedAt <= (tombstones.get(x.gameId) ?? -Infinity)) continue;
        tombstones.set(x.gameId, x.updatedAt);
        for (const [id, l] of lines) if (gameOf(l) === x.gameId && l.updatedAt < x.updatedAt) lines.delete(id);
        continue;
      }
      if (closed(tombstones, x)) continue;
      const h = lines.get(lineId(x));
      if (!h || x.updatedAt > h.updatedAt) lines.set(lineId(x), x);
    }
  }
  return { ...held, lines, tombstones };
}

// Players the deltas named that the projections don't hold (and haven't marked absent): deltas carry no player fields,
// so a seed must bring them.
export function unknownPlayers(projections: BoardProjections, deltas: readonly Delta[]): Set<string> {
  const unknown = new Set<string>();
  for (const d of deltas) for (const x of d.propLines) {
    if (isTombstone(x)) continue;
    const k = playerOf(x);
    if (!projections.players.has(k) && !projections.absent.has(k)) unknown.add(k);
  }
  return unknown;
}

export const composeBoard = (lines: BoardLines, projections: BoardProjections): Board =>
  ({ ...projections, lines: lines.lines });
